// Boss fights: one boss at the end of each job tier. Beat them in one go.
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, duration } from '../state.tsx';
import { Loading, useLoad, go, Panel, ItemIcon, ItemName } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { StatIcon } from '../art/Scenes.tsx';
import { play, sequence } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  return <div className="hp-bar"><div style={{ width: `${Math.max(0, Math.min(100, (value / Math.max(1, max)) * 100))}%`, background: color }} /><span>{fmt(value)} / {fmt(max)}</span></div>;
}

export function BossesTab() {
  const { me, act, setFlash, content, byId } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/bosses'), []);
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // The last exchange of blows, for the shake and damage-number animation.
  const [blow, setBlow] = useState<{ n: number; dealt: number; taken: number; phase: 'boss' | 'me' } | null>(null);
  const bosses: Map<string, any> = new Map(content.bosses.map((b: any) => [b.id, b]));
  const rules = content.rules.boss;
  useEffect(() => {
    if (!data?.fight) setLog([]);
  }, [data?.fight?.bossId]);
  if (!data) return <Loading />;

  const start = async (bossId: string) => {
    if (busy) return;
    setBusy(true);
    const r = await act('/api/bosses/start', { bossId });
    setBusy(false);
    if (r) {
      play('roar');
      setLog([`You tracked down ${bosses.get(bossId).name}. ${bosses.get(bossId).taunt}`]);
      reload();
    }
  };
  const hit = async (move: 'attack' | 'power' | 'crew') => {
    if (busy) return;
    setBusy(true);
    const r = await act('/api/bosses/attack', { move });
    setBusy(false);
    if (!r) return;
    const b = bosses.get(r.bossId);
    const n = Date.now();
    setBlow({ n, dealt: r.damageDealt, taken: r.damageTaken, phase: 'boss' });
    play(move === 'crew' ? 'swords' : 'bossHit');
    if (r.outcome !== 'won' && r.damageTaken > 0) setTimeout(() => { setBlow((x) => (x && x.n === n ? { ...x, phase: 'me' } : x)); play('punch'); }, 380);
    if (r.outcome === 'won') {
      sequence([[500, 'roar'], [1100, 'cheer'], [1300, 'chaching']]);
      setTimeout(() => { fx.coins(20); fx.float(`+${money(r.cash)}`, 'cash'); fx.float(`+${r.xp} XP`, 'xp', null, 180); }, 1200);
    }
    if (r.outcome === 'lost') sequence([[600, 'groan']]);
    const label = move === 'power' ? 'Power attack' : move === 'crew' ? 'Your crew' : 'You';
    const line = r.outcome === 'won'
      ? `${label} dealt ${r.damageDealt} damage. ${b.name} is down!`
      : `${label} dealt ${r.damageDealt} damage. ${b.name} hit back for ${r.damageTaken}.`;
    setLog((l) => [line, ...l].slice(0, 8));
    if (r.outcome === 'won') {
      setFlash({
        kind: 'good',
        title: `You beat ${b.name}!`,
        text: (
          <>
            You took <span className="money">{money(r.cash)}</span> and <span className="xp">{r.xp} experience</span>.
            {r.itemId && <div className="item-cell" style={{ marginTop: 6 }}><ItemIcon itemId={r.itemId} size={40} /> Boss loot: <ItemName itemId={r.itemId} /></div>}
            {r.levelUps.map((u: any) => <div key={u.level} className="gold">LEVEL UP! You are now level {u.level}.</div>)}
          </>
        ),
      });
    } else if (r.outcome === 'lost') {
      setFlash({ kind: 'bad', title: `${b.name} beat you.`, text: <>Your health dropped below {rules.minHealth}. The boss is back at full strength. Heal up at the <a onClick={() => go('hospital')}>hospital</a> and try again.</> });
    }
    reload();
  };
  const retreat = async () => {
    if (!confirm('Back off? The boss goes back to full health and you lose the energy you spent finding them.')) return;
    await act('/api/bosses/retreat');
    reload();
  };

  const f = data.fight;
  if (f) {
    const b = bosses.get(f.bossId);
    return (
      <div>
        <div className="boss-arena">
          <div className={`boss-side ${blow?.phase === 'boss' ? 'hit' : ''}`} key={blow?.phase === 'boss' ? `b${blow.n}` : 'b'}>
            <div className="clash-portrait">
              <Portrait seed={b.portrait} size={90} />
              {blow?.phase === 'boss' && blow.dealt > 0 && <span className="clash-dmg">-{fmt(blow.dealt)}</span>}
              {blow?.phase === 'boss' && <span className="clash-flash" />}
            </div>
            <div className="boss-name">{b.name}</div>
            <div className="small dim">{b.title}</div>
            <Bar value={f.bossHealth} max={f.bossMaxHealth} color="#c22" />
          </div>
          <div className="boss-vs">VS</div>
          <div className={`boss-side ${blow?.phase === 'me' ? 'hit' : ''}`} key={blow?.phase === 'me' ? `m${blow.n}` : 'm'}>
            <div className="clash-portrait">
              <Portrait seed={me.portrait} playerId={me.id} size={90} />
              {blow?.phase === 'me' && blow.taken > 0 && <span className="clash-dmg">-{fmt(blow.taken)}</span>}
              {blow?.phase === 'me' && <span className="clash-flash" />}
            </div>
            <div className="boss-name">{me.name}</div>
            <div className="small dim">You lose if your health drops below {f.minHealth}</div>
            <Bar value={me.health} max={me.maxHealth} color="#3a3" />
          </div>
        </div>
        <div className="boss-moves">
          <button className={`btn big ${me.stamina >= 1 ? '' : 'disabled'}`} disabled={busy} onClick={() => hit('attack')}>Attack <span className="small">(1 stamina)</span></button>
          <button className={`btn big red ${me.stamina >= rules.powerStamina ? '' : 'disabled'}`} disabled={busy} onClick={() => hit('power')} title={`${rules.powerMultiplier}× the damage, but they hit back just as hard`}>Power Attack <span className="small">({rules.powerStamina} stamina)</span></button>
          <button className={`btn big gray ${me.energy >= rules.crewEnergyCost ? '' : 'disabled'}`} disabled={busy} onClick={() => hit('crew')}>Send the Crew <span className="small">({rules.crewEnergyCost} energy)</span></button>
        </div>
        <div className="small muted center" style={{ margin: '6px 0' }}>
          Stamina {me.stamina} · Energy {me.energy} · Time left: {duration(Math.max(0, f.expiresAt - Date.now()))} · <a onClick={retreat}>back off</a>
        </div>
        <Panel title="Fight log">{log.length ? log.map((l, i) => <div key={i} className={i === 0 ? '' : 'dim'}>{l}</div>) : <div className="muted">Make your move.</div>}</Panel>
      </div>
    );
  }

  return (
    <div>
      <p className="muted">
        Every job tier ends with a boss. Do each job in a tier at least once to find them. Tracking a boss down costs energy; then hit them with
        stamina (or send your crew in for {rules.crewEnergyCost} energy a hit). You have to win in one go: if your health drops below {rules.minHealth} or
        {' '}{rules.timeLimitMinutes} minutes pass, the boss gets away and is back at full health. The first win always drops the boss’s unique item.
      </p>
      <div className="row" style={{ gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <span><StatIcon kind="health" /> Health <b>{me.health}</b></span>
        <span><StatIcon kind="stamina" /> Stamina <b>{me.stamina}</b></span>
      </div>
      <div className="boss-list">
        {data.bosses.map((s: any) => {
          const b = bosses.get(s.bossId);
          const tier = byId.tiers.get(b.tierId);
          const staminaOk = me.stamina + Math.floor(Math.max(0, me.energy - b.energyCost) / rules.crewEnergyCost) >= s.hitsNeeded;
          const healthOk = me.health - rules.minHealth > s.roundDamage * s.hitsNeeded;
          return (
            <div key={b.id} className={`boss-card ${s.unlocked ? '' : 'locked'}`}>
              <Portrait seed={b.portrait} size={64} />
              <div className="grow">
                <div className="job-name">{b.name} {s.wins > 0 && <span className="gold small">★ beaten {s.wins}×</span>}</div>
                <div className="small dim">{tier.name} boss · {b.title}</div>
                {s.unlocked ? (
                  <div className="small" style={{ marginTop: 3 }}>
                    About <b>{s.hitsNeeded}</b> hit{s.hitsNeeded === 1 ? '' : 's'} at your strength · they hit back ~<b>{s.roundDamage}</b> a round
                    {(!staminaOk || !healthOk) && <div className="bad">{!staminaOk ? 'You may not have enough stamina saved up. ' : ''}{!healthOk ? 'You may not survive that many rounds.' : ''}</div>}
                  </div>
                ) : <div className="small bad" style={{ marginTop: 3 }}>Do every {tier.name} job at least once to find them (level {tier.minLevel}+).</div>}
                <div className="small" style={{ marginTop: 3 }}>
                  Reward: <span className="money">~{money(b.cash)}</span> · <span className="xp">{b.xp} XP</span> · <ItemName itemId={b.itemId} />{s.wins > 0 ? <span className="dim"> (small chance to drop again)</span> : <span className="dim"> (guaranteed the first time)</span>}
                </div>
              </div>
              <div className="boss-action">
                <ItemIcon itemId={b.itemId} size={44} />
                {!s.unlocked ? <span className="small dim">Locked</span>
                  : s.cooldownMs > 0 ? <span className="small dim">Back in {duration(s.cooldownMs)}</span>
                  : <button className={`btn ${me.energy >= b.energyCost && me.health >= rules.minHealth + 5 ? '' : 'disabled'}`} disabled={busy} onClick={() => start(b.id)}>Fight ({b.energyCost} energy)</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Small card at the bottom of a job tier, pointing to that tier's boss. */
export function TierBossCard({ tierId, jobs }: { tierId: string; jobs: any[] }) {
  const { content } = useGame();
  const b = content.bosses.find((x: any) => x.tierId === tierId);
  if (!b) return null;
  const done = jobs.filter((j) => j.timesDone > 0).length;
  const unlocked = done >= jobs.length;
  return (
    <div className={`boss-card tier-boss ${unlocked ? '' : 'locked'}`}>
      <Portrait seed={b.portrait} size={48} />
      <div className="grow">
        <div className="job-name">Tier boss: {b.name}</div>
        <div className="small dim">{unlocked ? b.taunt : `Do every job in this tier at least once to find them (${done}/${jobs.length} done).`}</div>
      </div>
      <ItemIcon itemId={b.itemId} size={40} />
      <button className={`btn ${unlocked ? 'red' : 'disabled'}`} onClick={() => unlocked && go('fight/bosses')}>{unlocked ? 'Take them on' : 'Locked'}</button>
    </div>
  );
}
